import { useId, useRef, useState } from "react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { formatError } from "./api";
import { useDemoStore } from "./store";

export type FeedbackLevel = "error" | "warning" | "success" | "info";
export type Feedback = { text: string; level: FeedbackLevel };
export type FieldRule = { field: string; aliases: string[]; conflict?: boolean; json?: boolean };
export type FieldBindings = Record<string, FieldRule[]>;

// Only validation errors and explicitly known uniqueness conflicts belong to fields.
// Permissions, stale versions, networking and unknown failures remain action errors.
export function resolveErrorFields(action: string, error: unknown, bindings: FieldBindings): string[] {
  const item = error as { status?: number; code?: string; message?: string; field?: string } | null;
  const rules = bindings[action] || bindings[action.split(":")[0]] || [];
  const expand = (field: string) => field.replace("{action}", action);
  if (error instanceof SyntaxError) return rules.filter((rule) => rule.json).map((rule) => expand(rule.field));
  if (item?.status === 409 && item.code === "conflict") return rules.filter((rule) => rule.conflict).map((rule) => expand(rule.field));
  if (![400, 422].includes(item?.status || 0) && item?.code !== "invalid_argument" && item?.code !== "validation_error") return [];
  const text = item?.field || item?.message || "";
  const matched = rules.filter((rule) => rule.aliases.some((alias) => new RegExp(`(^|[^a-zA-Z0-9_])${alias}([^a-zA-Z0-9_]|$)`, "i").test(text)));
  // An aggregate message naming several possible inputs is not a field diagnosis.
  return matched.length === 1 ? [expand(matched[0]!.field)] : [];
}

// Each operation owns its feedback, including asynchronous/background results.
// Never infer severity from translated text or the currently focused element.
export function useActionFeedback(bindings: FieldBindings = {}) {
  const [messages, setMessages] = useState<Record<string, Feedback | undefined>>({});
  const [fields, setFields] = useState<Record<string, Feedback | undefined>>({});
  const validators = useRef<Record<string, (() => string | undefined) | undefined>>({});
  const revisions = useRef<Record<string, number>>({});
  const pending = useRef<Record<string, Record<string, number>>>({});
  const clearField = (field: string) => {
    revisions.current[field] = (revisions.current[field] || 0) + 1;
    setFields((current) => ({ ...current, [field]: undefined }));
  };
  const setField = (field: string, text?: string) => setFields((current) => ({ ...current, [field]: text ? { text, level: "error" } : undefined }));
  const rulesFor = (action: string) => (bindings[action] || bindings[action.split(":")[0]] || []).map((rule) => rule.field.replace("{action}", action));
  const set = (action: string, text: string, level: FeedbackLevel) => {
    setMessages((current) => ({ ...current, [action]: text ? { text, level } : undefined }));
  };
  const clear = (action: string) => {
    setMessages((current) => ({ ...current, [action]: undefined }));
    setFields((current) => { const next = { ...current }; for (const field of rulesFor(action)) delete next[field]; return next; });
  };
  const begin = (action: string) => {
    clear(action);
    pending.current[action] = Object.fromEntries(rulesFor(action).map((field) => [field, revisions.current[field] || 0]));
    let valid = true;
    for (const field of rulesFor(action)) { const error = validators.current[field]?.(); if (error) { setField(field, error); valid = false; } }
    return valid;
  };
  const fail = (action: string, error: unknown) => {
    const targets = resolveErrorFields(action, error, bindings);
    if (!targets.length) { set(action, formatError(error), "error"); return; }
    setMessages((current) => ({ ...current, [action]: undefined }));
    for (const field of targets) {
      if (pending.current[action]?.[field] !== undefined && pending.current[action]![field] !== (revisions.current[field] || 0)) continue;
      setField(field, formatError(error));
    }
  };
  const clearAll = () => { setMessages({}); setFields({}); };
  const register = (field: string, validator?: () => string | undefined) => { validators.current[field] = validator; };
  return { messages, fields, set, clear, clearAll, clearField, setField, begin, fail, register };
}

type FieldProps = { feedback: ReturnType<typeof useActionFeedback>; field: string; json?: boolean };
type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
function useField({ feedback, field, json }: FieldProps) {
  const id = useId();
  const locale = useDemoStore((state) => state.locale);
  const error = feedback.fields[field];
  const validate = (element: FieldElement) => {
    if (element.disabled || element instanceof HTMLInputElement && element.readOnly) return;
    const zh = locale === "zh-CN";
    if (json) { try { JSON.parse(element.value); } catch { return zh ? "请输入有效的 JSON" : "Enter valid JSON"; } }
    if (element.validity.valid) return;
    if (element.validity.valueMissing) return zh ? "请填写此项" : "This field is required";
    if (element.validity.typeMismatch && element instanceof HTMLInputElement && element.type === "email") return zh ? "请输入有效的邮箱地址" : "Enter a valid email address";
    if (element.validity.rangeUnderflow || element.validity.rangeOverflow || element.validity.stepMismatch || element.validity.badInput) return zh ? "请输入符合范围要求的数字" : "Enter a number within the allowed range";
    return zh ? "请检查此项的格式" : "Check this field's format";
  };
  return {
    id, error,
    ref: (element: FieldElement | null) => feedback.register(field, element ? () => validate(element) : undefined),
    change: () => feedback.clearField(field),
    blur: (element: FieldElement) => { const text = validate(element); if (text) feedback.setField(field, text); },
  };
}

export function FeedbackInput({ feedback, field, json, onChange, onBlur, ...props }: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const state = useField({ feedback, field, json });
  return <span className="feedback-field"><input {...props} ref={state.ref} aria-invalid={state.error ? true : undefined} aria-describedby={[props["aria-describedby"], state.error && state.id].filter(Boolean).join(" ") || undefined} onChange={(event) => { state.change(); onChange?.(event); }} onBlur={(event) => { state.blur(event.currentTarget); onBlur?.(event); }} /><FieldMessage id={state.id} value={state.error} /></span>;
}

export function FeedbackSelect({ feedback, field, onChange, onBlur, children, ...props }: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const state = useField({ feedback, field });
  return <span className="feedback-field"><select {...props} ref={state.ref} aria-invalid={state.error ? true : undefined} aria-describedby={state.error ? state.id : props["aria-describedby"]} onChange={(event) => { state.change(); onChange?.(event); }} onBlur={(event) => { state.blur(event.currentTarget); onBlur?.(event); }}>{children}</select><FieldMessage id={state.id} value={state.error} /></span>;
}

export function FeedbackTextarea({ feedback, field, json, onChange, onBlur, ...props }: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const state = useField({ feedback, field, json });
  return <span className="feedback-field"><textarea {...props} ref={state.ref} aria-invalid={state.error ? true : undefined} aria-describedby={state.error ? state.id : props["aria-describedby"]} onChange={(event) => { state.change(); onChange?.(event); }} onBlur={(event) => { state.blur(event.currentTarget); onBlur?.(event); }} /><FieldMessage id={state.id} value={state.error} /></span>;
}

function FieldMessage({ id, value }: { id: string; value?: Feedback }) {
  return value ? <span id={id} className="action-feedback field-error error" role="alert">{value.text}</span> : null;
}

export function InlineFeedback({ value }: { value?: Feedback }) {
  if (!value) return null;
  return <p className={`action-feedback ${value.level}`} role={value.level === "error" ? "alert" : "status"} aria-atomic="true">{value.text}</p>;
}

export function FeedbackAction({ value, children }: { value?: Feedback; children: ReactNode }) {
  return <div className="feedback-action">{children}<InlineFeedback value={value} /></div>;
}
